// Lightweight auth utilities — JWT decode + role helpers.
// No external dependency: native atob handles the base64url payload.

export const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
export const ID_CLAIM = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier';

const base64UrlDecode = (str) => {
  const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4));
  const b64 = (str + pad).replace(/-/g, '+').replace(/_/g, '/');
  try {
    return decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch {
    return atob(b64);
  }
};

export const jwtDecode = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    return JSON.parse(base64UrlDecode(parts[1]));
  } catch {
    return null;
  }
};

export const extractRoles = (token) => {
  const decoded = jwtDecode(token);
  if (!decoded) return [];
  const raw = decoded[ROLE_CLAIM] ?? decoded.role ?? decoded.roles;
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [raw];
};

export const extractUserId = (token) => {
  const decoded = jwtDecode(token);
  if (!decoded) return null;
  return decoded.sub ?? decoded[ID_CLAIM] ?? decoded.userId ?? null;
};

// Persist token + user object (with roles + userId) into localStorage.
// Pass `extra` for fields the backend returned (fullName, email, isNewUser, …).
export const persistAuth = (token, extra = {}) => {
  if (!token) return { roles: [], userId: null };
  localStorage.setItem('token', token);
  const roles = extractRoles(token);
  const userId = extractUserId(token);
  const user = {
    fullName: extra.fullName ?? null,
    email: extra.email ?? null,
    userId,
    roles,
    emailConfirmed: extra.emailConfirmed ?? true,
  };
  localStorage.setItem('user', JSON.stringify(user));
  return { roles, userId };
};

export const setEmailConfirmedFlag = (value) => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return;
    const u = JSON.parse(raw);
    u.emailConfirmed = !!value;
    localStorage.setItem('user', JSON.stringify(u));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth:user-updated'));
    }
  } catch {}
};

export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getRoles = () => {
  const u = getStoredUser();
  if (u?.roles?.length) return u.roles;
  // Fallback: derive from current token if user object is stale.
  const token = localStorage.getItem('token');
  return token ? extractRoles(token) : [];
};

export const hasRole = (role) => {
  if (!role) return false;
  const list = getRoles();
  if (Array.isArray(role)) return role.some((r) => list.includes(r));
  return list.includes(role);
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  if (typeof window !== 'undefined') window.location.href = '/login';
};

// Old tokens issued before role support won't carry the role claim.
// Force re-login so users always have a fresh token.
export const enforceRoleClaim = () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  const roles = extractRoles(token);
  if (roles.length === 0) {
    logout();
  } else {
    // Backfill the user object if it's missing roles (post-deploy migration).
    const u = getStoredUser();
    if (!u?.roles || u.roles.length === 0) {
      persistAuth(token, { fullName: u?.fullName, email: u?.email });
    }
  }
};
